import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    name: obj?.name,
    vehicleType: obj?.vehicle_type,
    brand: obj?.brand,
    model: obj?.model,
    year: obj?.year,
    licensePlate: obj?.license_plate,
    seats: obj?.seats,
    transmission: obj?.transmission,
    fuelType: obj?.fuel_type,
    dailyRate: obj?.daily_rate,
    dailyRateUsd: obj?.daily_rate_usd,
    image: obj?.image,
    imageAlt: obj?.image_alt,
    status: obj?.status,
    features: obj?.features || [],
    mileage: obj?.mileage,
    description: obj?.description,
    createdBy: obj?.created_by,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at,
  };
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  return {
    name: obj?.name,
    vehicle_type: obj?.vehicleType,
    brand: obj?.brand,
    model: obj?.model,
    year: obj?.year,
    license_plate: obj?.licensePlate,
    seats: obj?.seats,
    transmission: obj?.transmission,
    fuel_type: obj?.fuelType,
    daily_rate: obj?.dailyRate,
    daily_rate_usd: obj?.dailyRateUsd,
    image: obj?.image,
    image_alt: obj?.imageAlt,
    status: obj?.status,
    features: obj?.features,
    mileage: obj?.mileage,
    description: obj?.description,
  };
};

export const vehicleService = {
  async getAll() {
    const { data, error } = await supabase
      ?.from('vehicles')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async getAvailable() {
    const { data, error } = await supabase
      ?.from('vehicles')
      ?.select('*')
      ?.eq('status', 'available')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('vehicles')
      ?.select('*')
      ?.eq('id', id)
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async create(vehicle) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('vehicles')
      ?.insert({ ...toSnakeCase(vehicle), created_by: user?.id })
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async update(id, vehicle) {
    const { data, error } = await supabase
      ?.from('vehicles')
      ?.update(toSnakeCase(vehicle))
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('vehicles')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  async checkAvailability(vehicleId, startDate, endDate) {
    const { data, error } = await supabase?.rpc('is_vehicle_available', {
      vehicle_uuid: vehicleId,
      check_start_date: startDate,
      check_end_date: endDate,
    });

    if (error) throw error;
    return data;
  },

  subscribeToChanges(callback) {
    const channel = supabase
      ?.channel('vehicles')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'vehicles' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },
};